import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import {AuthResponse, UserRead} from "../schemas";

import {fetchOrStoreUser, login as loginApi, loginGoogle as loginGoogleApi, loginMicrosoft as loginMicrosoftApi, authenticateOAuth as authenticateOAuthApi, verifyEmailCode as verifyEmailCodeApi, logout as logoutApi, completeUserTutorial as completeUserTutorialApi, deleteAccount as deleteAccountApi} from "../api";
import jwtDecode from "jwt-decode";
import * as moment from "moment/moment";
import {withApiErrorHandler, withApiErrorHandlerThrow} from "../error_handling";


export interface UserProviderContextType {
    currentUser: UserRead | null | undefined;
    setCurrentUser: (User: UserRead) => void;
    isLoggedIn: boolean;
    logout: () => void;
    login: (email: string, password: string) => Promise<boolean>;
    loginGoogle: () => void;
    loginMicrosoft: () => void;
    authenticateOAuth: (code: string) => Promise<AuthResponse>;
    verifyEmailCode: (code: string, pendingAuthenticationToken: string) => Promise<UserRead>;
    completeUserTutorial(): void;
    deleteAccount: () => Promise<void>;
}

export const UserProviderContext = createContext<UserProviderContextType | undefined>(undefined);

interface DecodedAccessToken {
    exp: number;
    // add other properties from your token here
}

interface EnhancedDocumentsProviderProps {
    children: ReactNode;
}

export const UserProvider: React.FC<EnhancedDocumentsProviderProps> = ({
                                                                           children,
                                                                       }) => {
    const [currentUser, setCurrentUser] = useState<UserRead | undefined | null>(undefined);
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);

    useEffect(() => {
        const tokenString = localStorage.getItem("token");
        if (tokenString) {
            try {
                const decodedAccessToken: DecodedAccessToken = jwtDecode(tokenString);
                if (moment.unix(decodedAccessToken.exp).toDate() > new Date()) {
                    setIsLoggedIn(true);
                    const fetchUser = async () => {
                        const user: UserRead | null = await withApiErrorHandler(
                            () => fetchOrStoreUser(),
                            {titleQualifier: "Error Fetching User"}
                        );
                        if (user) {
                            setCurrentUser(user);
                        } else {
                            // API call failed (likely 401) - clear auth state
                            localStorage.removeItem('token');
                            localStorage.removeItem('user');
                            setIsLoggedIn(false);
                            setCurrentUser(null);
                        }
                    }
                    fetchUser();
                } else {
                    // Token expired - clear it
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    setIsLoggedIn(false);
                    setCurrentUser(null);
                }
            } catch (e) {
                // Invalid token - clear it
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                setIsLoggedIn(false);
                setCurrentUser(null);
            }
        } else {
            setIsLoggedIn(false);
            setCurrentUser(null);
        }
    }, []);

    const login = async (email: string, password: string): Promise<boolean> => {
        const user: UserRead | null = await withApiErrorHandler<UserRead>(
            () => loginApi(email, password),
            {titleQualifier: "Login"});
        setCurrentUser(user);
        setIsLoggedIn(!!user);
        return !!user;
    }
    const loginGoogle = () => {
        withApiErrorHandlerThrow(
            () => loginGoogleApi(),
            {titleQualifier: "Error Redirecting to Google"}
        );
    }

    const loginMicrosoft = () => {
        withApiErrorHandlerThrow(
            () => loginMicrosoftApi(),
            {titleQualifier: "Error Redirecting to Microsoft"}
        );
    }

    const logout = () => {
        withApiErrorHandler(
            () => logoutApi(),
            {titleQualifier: "Logout"});
        setIsLoggedIn(false);
        setCurrentUser(null);
    };

    const authenticateOAuth = async (code: string): Promise<AuthResponse> => {
        const authResponse: AuthResponse = await withApiErrorHandlerThrow<AuthResponse>(
            () => authenticateOAuthApi(code),
            {titleQualifier: "Authentication Error"});

        // Only set user if email verification is not required
        if (!authResponse.email_verification_required && authResponse.user) {
            setCurrentUser(authResponse.user);
            setIsLoggedIn(true);
            // Remember that a demo group is cloning so the dashboard can show a banner.
            if (authResponse.demo_seeding && authResponse.demo_group_id) {
                localStorage.setItem("demoSeedingGroupId", authResponse.demo_group_id);
            }
        }
        return authResponse;
    }

    const verifyEmailCode = async (code: string, pendingAuthenticationToken: string): Promise<UserRead> => {
        // Don't use error handler here - let the component handle errors inline
        const user: UserRead = await verifyEmailCodeApi(code, pendingAuthenticationToken);
        setCurrentUser(user);
        setIsLoggedIn(true);
        return user;
    }

    const completeUserTutorial = async  () => {
        const user = await completeUserTutorialApi();
        setCurrentUser(user);
    }

    // Permanently delete the account, then clear all local auth state (same teardown
    // as logout) so the app drops back to the logged-out experience.
    const deleteAccount = async () => {
        await deleteAccountApi();
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("demoSeedingGroupId");
        setIsLoggedIn(false);
        setCurrentUser(null);
    };

    const contextValue: UserProviderContextType = {
        currentUser,
        setCurrentUser,
        isLoggedIn,
        logout,
        login,
        loginGoogle,
        loginMicrosoft,
        authenticateOAuth,
        verifyEmailCode,
        completeUserTutorial,
        deleteAccount,
    };

    return (
        <UserProviderContext.Provider value={contextValue}>
            {children}
        </UserProviderContext.Provider>
    );
};

export const useUserContext = () => useContext(UserProviderContext);
